import WrapperLink from '@/components/link/WrapperLink';
import { generatePathname } from '@/helpers/generatePathname';
import useToast from '@/hooks/useToast';
import { useMediaQuery } from '@mui/material';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useContext, useState } from 'react';
import { BsDot } from 'react-icons/bs';
import { FiMoreHorizontal } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import { deletePostHard } from '../../../../apis/tms/post.api';
import { newTimeStamp } from '../../../../common/timestamp';
import { PATH_NAME } from '../../../../configs/pathName';
import { DEFAULT_AVATAR } from '../../../../constant';
import { FEED_POST_DETAIL } from '../../../../constant/QueryKeyName';
import { EditComment, TrashNoColorIcon } from '../../../../constant/imgImport';
import { ROLE_ACCESS } from '../../../../constant/storage';
import { PostContext } from '../../../../context/PostContext';
import { routerToBack } from '../../../../helpers/checkRouterPrevPath';
import { delayCallback } from '../../../../helpers/delayNavigate';
import {
  EPostEvent,
  postMessagePostId,
} from '../../../../hooks/post/usePostManagement';
import BlueTick from '../../profile/BlueTick';

const PostModal = dynamic(
  () => import('../../../modal/training-module/post/PostModal')
);
const ConfirmModal = dynamic(
  () => import('../../../modal/training-module/ConfirmModal')
);
const Drawer = dynamic(() => import('@mui/material/Drawer'));
const Menu = dynamic(() => import('@mui/material/Menu'));
const MenuItem = dynamic(() => import('@mui/material/MenuItem'));
const Typography = dynamic(() => import('@mui/material/Typography'));
const ListItemText = dynamic(() => import('@mui/material/ListItemText'));
const IconButton = dynamic(() => import('@mui/material/IconButton'));

const PostHead = ({ groupMode = false }: { groupMode?: boolean }) => {
  const { group, owner, ...postInfo } = useContext(PostContext);
  const matches = useMediaQuery('(min-width:460px)');

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  const router = useRouter();
  const { pid: postId, gid: groupId } = router.query;
  const queryClient = useQueryClient();

  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const toast = useToast();

  const onClose = () => {
    setOpen(false);
  };

  const handleDeletePost = async () => {
    try {
      await deletePostHard(postInfo.postId);
      postMessagePostId(postInfo.postId, EPostEvent.Delete);
      toast('Xoá bài viết thành công', 'success');

      delayCallback(() => {
        queryClient.refetchQueries([FEED_POST_DETAIL, postInfo.postId], {
          active: true,
        });
        !!postId && routerToBack(router, PATH_NAME.HOME_FEED);
      }, 500);
    } catch (error) {
      toast('Xoá bài viết thất bại. Vui lòng thử lại', 'error');
    }
    handleToggleConfirmDeleteModal();
  };

  const handleToggleEditPostModal = () => {
    setOpenEditModal((prev) => !prev);
  };

  const handleToggleConfirmDeleteModal = () => {
    setOpenConfirmDeleteModal((prev) => !prev);
  };

  const groupPathname = generatePathname({
    pathname: PATH_NAME.MY_GROUPS_DETAIL,
    query: {
      gid: group?.groupId || '',
    },
  });

  const profilePathname = generatePathname({
    pathname: PATH_NAME.PROFILE,
    query: {
      uid: owner.userId,
    },
  });

  const ownerAvatar = owner?.avatar || DEFAULT_AVATAR;
  const groupAvatar = group?.avatar || '/icons/default-group-avatar.svg';

  return (
    <>
      <div className="w-full py-4 pl-3 pr-2">
        <div className="flex w-full items-center justify-between gap-1">
          <div
            style={{ width: 'calc(100% - 40px)' }}
            className="flex flex-1 items-center gap-4"
          >
            <div className="relative flex h-auto min-w-[40px] max-w-[40px] items-center justify-center">
              {group ? (
                <>
                  <WrapperLink href={groupPathname}>
                    <Image
                      alt={!groupId ? 'group-avt' : 'owner-avt'}
                      src={!groupId ? groupAvatar : ownerAvatar}
                      width={40}
                      height={40}
                      className="cursor-pointer overflow-hidden rounded-full object-cover"
                    />
                  </WrapperLink>
                  {!groupId ? (
                    <WrapperLink
                      href={profilePathname}
                      anchorProps={{
                        className:
                          'absolute bottom-[-10%] left-[calc(50%-20px/2+14px)] top-[60%] h-5 w-5 cursor-pointer overflow-hidden rounded-full object-cover',
                      }}
                    >
                      <Image
                        alt="owner-avt"
                        src={ownerAvatar}
                        width={20}
                        height={20}
                      />
                    </WrapperLink>
                  ) : null}
                </>
              ) : (
                <>
                  <WrapperLink
                    href={profilePathname}
                    anchorProps={{
                      className:
                        'h-10 w-10 cursor-pointer rounded-full object-cover overflow-hidden',
                    }}
                  >
                    <Image
                      alt="owner-avt"
                      src={ownerAvatar}
                      width={40}
                      height={40}
                    />
                  </WrapperLink>
                </>
              )}
            </div>
            <div className="relative flex h-auto flex-shrink flex-grow flex-col truncate">
              <div className="flex w-full flex-1 items-center gap-1">
                <WrapperLink
                  href={groupMode || !group ? profilePathname : groupPathname}
                  anchorProps={{
                    className:
                      'cursor-pointer whitespace-normal text-lg font-bold leading-[160%] tracking-[-0.25px] text-[#242424]',
                  }}
                >
                  {groupMode || !group ? (
                    <>
                      {owner?.academicRank ? `${owner.academicRank}.` : ''}{' '}
                      {owner?.name}
                    </>
                  ) : (
                    group?.name
                  )}
                </WrapperLink>
                {groupMode || (!group && owner?.hasBlueTick && <BlueTick />)}
              </div>
              <div className="flex flex-wrap items-center">
                {!groupMode && group && (
                  <div className="flex max-w-[100%] items-center">
                    <WrapperLink
                      href={profilePathname}
                      anchorProps={{
                        className:
                          'w-fit max-w-[120px] cursor-pointer text-xs font-semibold leading-[130%] tracking-[-0.25px] text-grey_helper md:max-w-none',
                        style: {
                          whiteSpace: 'break-spaces',
                        },
                      }}
                    >
                      {owner?.academicRank ? `${owner.academicRank}.` : ''}{' '}
                      {owner?.name}
                    </WrapperLink>
                    {owner?.hasBlueTick && (
                      <div className="ml-1">
                        <BlueTick width={20} height={20} />
                      </div>
                    )}
                    <BsDot color="#707070" className="flex-shrink-0" />
                  </div>
                )}

                <p className="w-max flex-shrink-0 text-xs font-semibold leading-[130%] tracking-[-0.25px] text-grey_helper">
                  {newTimeStamp(postInfo.createdAt)}
                </p>
              </div>
            </div>
          </div>
          {(postInfo.isOwner || group?.userRole === ROLE_ACCESS.Owner) && (
            <IconButton
              id="more-icon-button"
              aria-controls={openMenu ? 'more-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openMenu ? 'true' : undefined}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                !matches ? setOpen(true) : setAnchorEl(event.currentTarget);
              }}
              className="!m-0 !w-[40px] !p-2"
            >
              <FiMoreHorizontal size={24} />
            </IconButton>
          )}
        </div>
      </div>
      {open && (
        <Drawer
          anchor={'bottom'}
          open={open}
          onClose={onClose}
          PaperProps={{
            square: false,
            style: {
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
              borderBottomLeftRadius: '0px',
              borderBottomRightRadius: '0px',
            },
          }}
        >
          <div className="flex h-full min-h-[30dvh] w-full max-w-[100vw] flex-col gap-4 px-4 pb-12 pt-6">
            <p className="text-xl font-bold leading-[25px] tracking-[-0.01em] text-[#242424]">
              Bài đăng
            </p>
            <div className="flex flex-col gap-4">
              {postInfo.isOwner && (
                <button
                  onClick={handleToggleEditPostModal}
                  className="flex items-center gap-3 p-[8px_16px] hover:bg-primary_supa_lighter"
                >
                  <Image src={EditComment} alt="edit" width={24} height={24} />
                  <span className="text-lg font-semibold leading-[150%] tracking-[-0.1px] text-grey_helper">
                    Chỉnh sửa
                  </span>
                </button>
              )}
              <button
                onClick={handleToggleConfirmDeleteModal}
                className="flex items-center gap-3 p-[8px_16px] hover:bg-primary_supa_lighter"
              >
                <Image
                  src={TrashNoColorIcon}
                  alt="delete"
                  width={24}
                  height={24}
                />
                <span className="text-lg font-semibold leading-[150%] tracking-[-0.1px] text-grey_helper">
                  Xoá bài đăng
                </span>
              </button>
            </div>
          </div>
        </Drawer>
      )}
      {openMenu && (
        <Menu
          id="more-menu"
          aria-labelledby="more-icon-button"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={() => {
            setAnchorEl(null);
          }}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '.MuiMenu-paper': {
              borderRadius: '10px !important',
              boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.15) !important',
            },
          }}
        >
          {postInfo.isOwner && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleToggleEditPostModal();
              }}
            >
              <ListItemText sx={{ color: '#707070', marginRight: 8 }}>
                Chỉnh sửa
              </ListItemText>
              <Typography variant="body2" color="text.secondary">
                <Image src={EditComment} alt="edit" width={18} height={18} />
              </Typography>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              handleToggleConfirmDeleteModal();
            }}
          >
            <ListItemText sx={{ color: '#707070', marginRight: 8 }}>
              Xoá bài đăng
            </ListItemText>
            <Typography variant="body2" color="text.secondary">
              <Image
                src={TrashNoColorIcon}
                alt="delete"
                width={18}
                height={18}
              />
            </Typography>
          </MenuItem>
        </Menu>
      )}

      {openEditModal && (
        <PostModal
          open={openEditModal}
          onClose={handleToggleEditPostModal}
          defaultData={{
            groupId: group?.groupId || 'null',
            content: postInfo.content,
            title: postInfo.title,
            hashtag: postInfo.hashTags.map((item) => ({
              name: item.name,
              value: item.id,
            })),
            postMode: postInfo.type,
            listImage: postInfo.images,
            postId: postInfo.postId,
            startDateTime: postInfo.startDate || '',
            endDateTime: postInfo.endDate || '',
            allowUploadedFile: postInfo.allowUploadedFile,
          }}
        />
      )}
      {openConfirmDeleteModal && (
        <ConfirmModal
          open={openConfirmDeleteModal}
          onClose={handleToggleConfirmDeleteModal}
          cancelCallback={handleDeletePost}
          confirmCallback={() => {}}
          title={'Xoá bài đăng'}
          description={'Bài đăng sẽ bị xoá vĩnh viễn. Bạn có muốn tiếp tục?'}
          confirmContent={'Huỷ'}
          cancelContent={'Xoá bài đăng'}
        />
      )}
    </>
  );
};

export default PostHead;
